import React, { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { initials } from '../lib/constants';
import { Activity, Plus, Move, Check, Undo2, Tag, Calendar, Paperclip, UserPlus, UserMinus, Trash2, AlertCircle, MessageSquare, AtSign } from 'lucide-react';

const ICONS = {
  created: Plus,
  moved: Move,
  completed: Check,
  reopened: Undo2,
  priority_changed: AlertCircle,
  label_changed: Tag,
  due_date_changed: Calendar,
  attachment_added: Paperclip,
  attachment_removed: Paperclip,
  checklist_added: Check,
  assignee_added: UserPlus,
  assignee_removed: UserMinus,
  delete_requested: Trash2,
  delete_approved: Trash2,
  delete_rejected: Trash2,
  title_changed: Activity,
  description_updated: Activity,
  commented: MessageSquare,
  mentioned: AtSign,
};

const TEXT = {
  created: () => `created this task`,
  moved: () => `moved this task`,
  completed: () => `marked this complete`,
  reopened: () => `re-opened this task`,
  priority_changed: (a) => `changed priority ${a.from_value ? `from ${a.from_value} ` : ''}to ${a.to_value}`,
  label_changed: () => `changed label`,
  due_date_changed: (a) => `set due date to ${a.to_value ? new Date(a.to_value).toLocaleDateString() : '—'}`,
  attachment_added: (a) => `attached "${a.to_value}"`,
  attachment_removed: (a) => `removed attachment "${a.from_value}"`,
  checklist_added: (a) => `added checklist "${a.to_value}"`,
  assignee_added: () => `added an assignee`,
  assignee_removed: () => `removed an assignee`,
  delete_requested: (a) => `requested delete${a.to_value ? ` — "${a.to_value}"` : ''}`,
  delete_approved: () => `approved delete`,
  delete_rejected: () => `rejected delete`,
  title_changed: (a) => `renamed to "${a.to_value}"`,
  description_updated: () => `updated the description`,
  deleted: () => `deleted this task`,
  commented: (a) => `commented${a.to_value ? `: "${a.to_value}"` : ''}`,
  mentioned: () => `mentioned someone`,
};

function timeAgo(d) {
  const diff = (Date.now() - new Date(d).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(d).toLocaleDateString();
}

const PAGE = 30;

export default function ActivityFeed({ taskId, refreshKey }) {
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadInitial = useCallback(async () => {
    try {
      const { data } = await api.get(`/tasks/${taskId}/activity?limit=${PAGE}`);
      setItems(data.activity || []);
      setHasMore(!!data.has_more);
    } catch { setItems([]); setHasMore(false); }
  }, [taskId]);

  useEffect(() => { if (taskId) loadInitial(); }, [taskId, refreshKey, loadInitial]);

  const loadMore = async () => {
    if (!items.length || loadingMore) return;
    setLoadingMore(true);
    try {
      const last = items[items.length - 1];
      const { data } = await api.get(`/tasks/${taskId}/activity?limit=${PAGE}&before=${encodeURIComponent(last.created_at)}`);
      setItems((prev) => [...prev, ...(data.activity || [])]);
      setHasMore(!!data.has_more);
    } finally { setLoadingMore(false); }
  };

  if (!items.length) return <div style={{ fontSize: 13, color: 'var(--trello-text-muted)' }}>No activity yet.</div>;
  return (
    <div style={{ maxHeight: 260, overflowY: 'auto', background: 'var(--trello-card-bg)', borderRadius: 6, padding: 8 }} data-testid="activity-feed">
      {items.map((a) => {
        const Icon = ICONS[a.action] || Activity;
        const fn = TEXT[a.action];
        const text = fn ? fn(a) : a.action.replace(/_/g, ' ');
        return (
          <div key={a.id} className="flex items-start gap-2" style={{ padding: '6px 2px' }} data-testid={`activity-${a.id}`}>
            <div style={{ background: a.actor_color || 'var(--trello-text-muted)', color: '#fff', width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0, marginTop: 2 }}>
              {initials({ full_name: a.actor_name })}
            </div>
            <div style={{ flex: 1, fontSize: 13 }}>
              <span style={{ fontWeight: 600 }}>{a.actor_name || 'System'}</span>{' '}<span>{text}</span>
              <div style={{ fontSize: 11, color: 'var(--trello-text-muted)', marginTop: 1 }}>
                <Icon size={10} style={{ display: 'inline', verticalAlign: -1, marginRight: 3 }} />
                {timeAgo(a.created_at)}
              </div>
            </div>
          </div>
        );
      })}
      {hasMore && (
        <button
          className="tm-btn-ghost"
          onClick={loadMore}
          disabled={loadingMore}
          data-testid="activity-load-more"
          style={{ width: '100%', justifyContent: 'center', padding: 8, fontSize: 12 }}
        >
          {loadingMore ? 'Loading…' : 'Load older activity'}
        </button>
      )}
    </div>
  );
}
